import React, { useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { Button } from "@/components/common/Button";
import { Card } from "@/components/common/Card";
import { Collapsible } from "@/components/common/Collapsible";
import { OptionCard } from "@/components/common/OptionCard";
import { TextInput } from "@/components/common/TextInput";
import { useAnswerQuestion, useDeleteQuestion } from "@/lib/api/apps/hooks";
import type { AnswerRequest, Question } from "@/lib/api/apps/types";
import { KIND_LABEL, whenLabel } from "../app-format";

/** An option by index, the person's own words, or the decision left to the agent. */
export type Choice = number | "own" | "skip";

/** What a choice sends: own words left empty are a skip. */
export function answerOf(choice: Choice, text: string): AnswerRequest {
  if (choice === "skip" || (choice === "own" && !text.trim()))
    return { skip: true };
  if (choice === "own") return { text: text.trim() };
  return { option: choice };
}

interface QuestionCardProps {
  appId: string;
  question: Question;
  /** Its place in a round, shown beside the question. */
  number?: number;
  /** A run's questions fold to one line in a long list. */
  open?: boolean;
  onToggle?: () => void;
  /** A round's question: every change goes up, and the round's answers are
   *  sent together. Without it, the card answers on its own. */
  onChange?: (answer: AnswerRequest) => void;
  /** The card's own answer was saved. */
  onSaved?: (questionId: string) => void;
}

/** One question, whoever asked it: what it is about, why the agent asks,
 *  the options with the agent's suggestion, the person's own words, Skip. */
export function QuestionCard({
  appId,
  question,
  number,
  open = true,
  onToggle,
  onChange,
  onSaved,
}: QuestionCardProps) {
  const [choice, setChoice] = useState<Choice>(question.pick);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [url, setUrl] = useState("");
  const [applySimilar, setApplySimilar] = useState(true);
  const answer = useAnswerQuestion(appId);
  const round = question.ask_round != null;
  const option = typeof choice === "number" ? question.options[choice] : null;
  const needsUrl = option?.needs_url ?? false;
  const urlOk = /^https?:\/\/\S+\.\S+/.test(url.trim());
  const skipped = choice === "skip";
  const choose = (next: Choice, words = text) => {
    setChoice(next);
    onChange?.(answerOf(next, words));
  };
  const meta = [
    round ? null : KIND_LABEL[question.kind],
    question.target_label || "The brief",
    question.holds_label || null,
  ]
    .filter(Boolean)
    .join(" · ");
  const yours = skipped
    ? "Skipped · the agent decides"
    : choice === "own"
      ? `Your answer: ${text.trim() || "…"}`
      : `Answer: ${option?.label}`;
  const title = (
    <span className="text-fg-title" style={{ textWrap: "pretty" }}>
      {question.title}
    </span>
  );

  return (
    <Card variant={skipped ? "subtle" : "default"} padded={false}>
      <div className="flex flex-col" style={{ padding: "var(--spacing-lg)" }}>
        <div className="flex items-start gap-3">
          {number != null && (
            <span
              className="flex flex-none items-center justify-center rounded-full bg-bg-subtle text-fg-muted-medium"
              style={{ width: "1.5rem", height: "1.5rem" }}
              aria-hidden
            >
              {number}
            </span>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {onToggle ? (
              // eslint-disable-next-line no-restricted-syntax -- the question folds and unfolds its card
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="flex items-start gap-2 text-left cursor-pointer"
              >
                <span className="flex-1">{title}</span>
                <ExpandMoreIcon
                  sx={{ fontSize: 18 }}
                  className="text-fg-muted"
                  style={{
                    transform: open ? "rotate(180deg)" : undefined,
                    transition:
                      "transform var(--duration-short) var(--ease-move)",
                  }}
                />
              </button>
            ) : (
              title
            )}
            <span className="text-fg-muted-regular-small">{meta}</span>
            {(!open || skipped) && (
              <span className="text-fg-regular">{yours}</span>
            )}
          </div>
        </div>

        <Collapsible
          open={open && !skipped}
          fade
          className="flex flex-col gap-3 pt-3"
        >
          {question.evidence.length > 0 && (
            <ul className="m-0 flex flex-col gap-1 p-0">
              {question.evidence.map((e, i) => (
                <li key={i} className="list-none text-fg-muted">
                  {e.what}
                  {e.source_url && (
                    <a
                      href={e.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 inline-flex items-center gap-1 text-small hover:text-accent hover:underline"
                    >
                      {hostOf(e.source_url)}
                      <OpenInNewOutlinedIcon className="icon-xs" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div
            role="radiogroup"
            aria-label={question.title}
            className="flex flex-col gap-1.5"
          >
            {question.options.map((o, i) => (
              <OptionCard
                key={i}
                selected={choice === i}
                disabled={Boolean(o.unavailable)}
                onSelect={() => choose(i)}
                title={
                  <>
                    <span className="flex-1">{o.label}</span>
                    {i === question.pick && (
                      <span className="text-accent-regular text-small">
                        Suggested
                      </span>
                    )}
                  </>
                }
                detail={o.unavailable ?? o.fact ?? o.effect_summary}
              />
            ))}
            <OptionCard
              selected={choice === "own"}
              onSelect={() => choose("own")}
              title="Something else"
              detail="Say it in your own words"
            />
            {choice === "own" && (
              <TextInput
                multiline
                minRows={2}
                value={text}
                onChange={(v) => {
                  setText(v);
                  onChange?.(answerOf("own", v));
                }}
                placeholder="Your answer"
                ariaLabel="Your own answer"
                autoFocus
              />
            )}
          </div>
          {needsUrl && (
            <TextInput
              value={url}
              onChange={setUrl}
              placeholder="https://…"
              ariaLabel="Source url"
              ariaInvalid={url.length > 0 && !urlOk}
            />
          )}
          {question.similar && (
            <label className="flex items-center gap-2 text-fg-muted-regular-small">
              <input
                type="checkbox"
                checked={applySimilar}
                onChange={(e) => setApplySimilar(e.target.checked)}
              />
              Apply to {question.similar.count} similar ·{" "}
              {question.similar.label}
            </label>
          )}
          {!onChange && typeof choice === "number" && (
            <TextInput
              value={note}
              onChange={setNote}
              placeholder="A note for the agent, optional"
              ariaLabel="Note"
            />
          )}
        </Collapsible>

        <Collapsible open={open || skipped} fade>
          <div className="flex flex-wrap items-center gap-3 pt-3">
            <span
              className={
                answer.error ? "text-error" : "text-fg-muted-regular-small"
              }
              style={
                answer.error
                  ? { fontSize: "var(--font-size-small)" }
                  : undefined
              }
            >
              {answer.error
                ? (answer.error as Error).message
                : onChange
                  ? ""
                  : question.fallback_label}
            </span>
            <span className="flex-1" />
            <Button
              variant="quiet-link"
              size="sm"
              onClick={() => choose(skipped ? question.pick : "skip")}
            >
              {skipped ? "Undo skip" : "Skip"}
            </Button>
            {!onChange && (
              <Button
                variant="primary"
                size="sm"
                loading={answer.isPending}
                disabled={
                  (needsUrl && !urlOk) || (choice === "own" && !text.trim())
                }
                onClick={() =>
                  answer.mutate(
                    {
                      questionId: question.question_id,
                      answer: {
                        ...answerOf(choice, text),
                        note:
                          typeof choice === "number"
                            ? note.trim() || undefined
                            : undefined,
                        apply_similar:
                          question.similar && typeof choice === "number"
                            ? applySimilar
                            : undefined,
                        url: needsUrl ? url.trim() : undefined,
                      },
                    },
                    { onSuccess: () => onSaved?.(question.question_id) },
                  )
                }
              >
                {choice === question.pick
                  ? "Confirm"
                  : skipped
                    ? "Leave it to the agent"
                    : "Answer"}
              </Button>
            )}
          </div>
        </Collapsible>
      </div>
    </Card>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** A saved answer in words, or `null` for one left to the agent. */
function answerText(question: Question): string | null {
  if (question.answer_kind === "own") return question.answer_note ?? "";
  if (question.answer_kind === "skipped" || question.state === "resolved")
    return null;
  return question.options[question.answer_option ?? question.pick]?.label ?? "";
}

/** A saved question in the log: the question, its answer, and when it was
 *  saved to the context. Edit changes the answer in the person's own words,
 *  Delete takes it out of what the runs read; `fresh` is one saved from this
 *  page just now. */
export function AnsweredRow({
  appId,
  question,
  fresh = false,
  onSaved,
}: {
  appId: string;
  question: Question;
  fresh?: boolean;
  onSaved?: (questionId: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [text, setText] = useState("");
  const answer = useAnswerQuestion(appId);
  const remove = useDeleteQuestion(appId);
  const saved = answerText(question);
  // Decided by the agent the moment it asked, and nobody has answered since.
  const autoPicked = question.state === "resolved" && !question.answer_kind;
  const edit = () => {
    setText(saved ?? "");
    answer.reset();
    setDeleting(false);
    setEditing(true);
  };
  const confirmDelete = () => {
    remove.reset();
    setEditing(false);
    setDeleting(true);
  };
  const change = () =>
    answer.mutate(
      { questionId: question.question_id, answer: { text: text.trim() } },
      {
        onSuccess: () => {
          setEditing(false);
          onSaved?.(question.question_id);
        },
      },
    );
  return (
    <div
      className="flex flex-col border-b border-border last:border-b-0"
      style={{ padding: "var(--spacing-sm) var(--spacing-md)" }}
    >
      <div className="flex items-start gap-3">
        <CheckCircleOutlineIcon
          className={`icon-sm text-success shrink-0 mt-0.5 ${fresh ? "request-landed-check" : ""}`}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-fg-muted">{question.title}</span>
          <span className="flex items-baseline gap-2">
            <span className="text-accent-regular text-small flex-none">
              Answer
            </span>
            <span className="text-fg-medium">
              {autoPicked
                ? `${question.options[question.pick]?.label ?? ""} · Auto-picked`
                : (saved ?? "Left to the agent · it decides and notes how")}
            </span>
          </span>
        </div>
        <span
          className={`shrink-0 ${fresh ? "text-success" : "text-fg-muted-regular-small"}`}
          role={fresh ? "status" : undefined}
        >
          {fresh
            ? "Saved"
            : question.settled_at
              ? whenLabel(question.settled_at)
              : ""}
        </span>
        {question.editable && (
          <>
            <Button
              variant="quiet-link"
              size="sm"
              disabled={editing}
              onClick={edit}
            >
              Edit
            </Button>
            <Button
              variant="quiet-link"
              size="sm"
              disabled={deleting}
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </>
        )}
      </div>
      {deleting && (
        <div className="flex flex-wrap items-center gap-2 pt-3">
          <span className="text-fg-medium flex-1">
            {remove.error
              ? (remove.error as Error).message
              : "Delete this clarification? The runs stop following it; what it already changed in the tables stays."}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDeleting(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            loading={remove.isPending}
            onClick={() => remove.mutate(question.question_id)}
          >
            Delete
          </Button>
        </div>
      )}
      {question.editable && (
        <Collapsible open={editing} fade className="flex flex-col gap-2 pt-3">
          <TextInput
            multiline
            minRows={2}
            value={text}
            onChange={setText}
            placeholder="Your answer"
            ariaLabel="Your answer"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-error flex-1">
              {answer.error ? (answer.error as Error).message : ""}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={answer.isPending}
              disabled={!text.trim() || text.trim() === saved}
              onClick={change}
            >
              Change
            </Button>
          </div>
        </Collapsible>
      )}
    </div>
  );
}
